import React, { useMemo, useState } from 'react';
import { addDays, format, isWeekend } from 'date-fns';
import { nl } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, LoaderCircleIcon } from 'lucide-react';
import type { House } from '../../types/house';
import type { Advice } from '../../utils/energy';
import { euro, dec } from '../../utils/format';
import { demoContact } from '../../data/demo';
interface StepContactProps {
  house: House;
  advice: Advice;
  onBack: () => void;
}
type Moment = 'ochtend' | 'middag' | 'avond';
type Status = 'idle' | 'submitting' | 'done';
interface FormState {
  name: string;
  email: string;
  phone: string;
  note: string;
  consent: boolean;
}
const moments: {
  value: Moment;
  label: string;
}[] = [{
  value: 'ochtend',
  label: '09:00 – 12:00'
}, {
  value: 'middag',
  label: '13:00 – 17:00'
}, {
  value: 'avond',
  label: '18:00 – 20:00'
}];
export function StepContact({
  house,
  advice,
  onBack
}: StepContactProps) {
  const days = useMemo(() => {
    const result: Date[] = [];
    let d = addDays(new Date(), 2);
    while (result.length < 6) {
      if (!isWeekend(d)) result.push(d);
      d = addDays(d, 1);
    }
    return result;
  }, []);
  const [dayIndex, setDayIndex] = useState(0);
  const [moment, setMoment] = useState<Moment>('ochtend');
  const [mode, setMode] = useState<'thuis' | 'online'>('thuis');
  const [form, setForm] = useState<FormState>(() => house.source === 'demo' ? {
    ...demoContact,
    consent: true
  } : {
    name: '',
    email: '',
    phone: '',
    note: '',
    consent: false
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const set = <K extends keyof FormState,>(key: K, value: FormState[K]) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.name.trim().length < 2) next.name = 'Vul je naam in.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Vul een geldig e-mailadres in.';
    if (form.phone.replace(/\D/g, '').length < 10) next.phone = 'Vul een telefoonnummer van 10 cijfers in.';
    if (!form.consent) next.consent = 'Geef toestemming zodat we contact kunnen opnemen.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('done'), 1200);
  };
  const dayLabel = format(days[dayIndex], 'EEEE d MMMM', {
    locale: nl
  });
  const momentLabel = moments.find(m => m.value === moment)?.label;
  if (status === 'done') {
    return <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} role="status" data-id="element-537" data-mp="3a55q5m3m3wmd">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-volt text-ink" data-id="element-538" data-mp="1yq0jk70d1dxh">
          <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-539" data-mp="1oo6cfglicidh" />
        </span>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-540" data-mp="1tlomxbf6b5nl">
          Tot {dayLabel}, {form.name.split(' ')[0]}.
        </h1>
        <p className="mt-3 max-w-lg text-lg text-ink-soft" data-id="element-541" data-mp="1yjzxjodalk6v">
          Je afspraak staat ingepland. Je ontvangt binnen een paar minuten een bevestiging op {form.email}.
        </p>
        <dl className="mt-8 divide-y divide-line rounded-3xl border border-line bg-white px-6 text-[15px]" data-id="element-542" data-mp="27ctf5sg7ovnw">
          <div className="flex justify-between gap-4 py-4" data-id="element-543" data-mp="2cb4p036ib664">
            <dt className="text-ink-muted" data-id="element-544" data-mp="2hb94bpvohuur">Afspraak</dt>
            <dd className="text-right font-medium capitalize text-ink" data-id="element-545" data-mp="2h9pnny641hsg">{dayLabel}, {momentLabel}</dd>
          </div>
          <div className="flex justify-between gap-4 py-4" data-id="element-546" data-mp="mxrh63z6r009">
            <dt className="text-ink-muted" data-id="element-547" data-mp="mzccvwx8kozp">Waar</dt>
            <dd className="text-right font-medium text-ink" data-id="element-548" data-mp="rwky66dol956">
              {mode === 'thuis' ? `${house.street} ${house.houseNumber}, ${house.city}` : 'Online videogesprek'}
            </dd>
          </div>
          <div className="flex justify-between gap-4 py-4" data-id="element-549" data-mp="2dfelzn8t1ex1">
            <dt className="text-ink-muted" data-id="element-550" data-mp="2dgzel7bk6ns9">Advies</dt>
            <dd className="text-right font-medium text-ink" data-id="element-551" data-mp="2rdl9qak1hyiz">
              {advice.product.name} · {dec(advice.product.capacity)} kWh · {euro(advice.savingsTotal)} per jaar
            </dd>
          </div>
        </dl>
      </motion.div>;
  }
  const input = (hasError: boolean) => `h-12 w-full rounded-xl border bg-white px-4 text-[16px] text-ink outline-none transition-colors duration-150 focus:border-ink ${hasError ? 'border-danger' : 'border-line'}`;
  return <form onSubmit={handleSubmit} noValidate data-id="element-552" data-mp="23ok04jzh0hc4">
      <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-553" data-mp="23nqzm0r3uo2f">Plan je gratis adviesgesprek.</h1>
      <p className="mt-3 text-lg text-ink-soft" data-id="element-554" data-mp="1fx4pm3xjitps">
        Een adviseur neemt je berekening voor de {advice.product.name} door en bekijkt de beste plek voor de batterij.
      </p>

      <fieldset className="mt-8" data-id="element-555" data-mp="3mmghip13g8ez">
        <legend className="text-[15px] font-medium text-ink" data-id="element-556" data-mp="3regfibxlsr8x">Waar?</legend>
        <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-557" data-mp="3rgkrzi9kiykt">
          {(['thuis', 'online'] as const).map(m => <label key={m} className={`cursor-pointer rounded-2xl border bg-white p-4 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${mode === m ? 'border-ink' : 'border-line hover:border-ink/40'}`} data-id="element-558" data-mp="3dl8ksbe87fn0">
              <input type="radio" name="mode" className="sr-only" checked={mode === m} onChange={() => setMode(m)} data-id="element-559" data-mp="e6n26dii12zi" />
              <span className="block text-[15px] font-medium text-ink" data-id="element-560" data-mp="7x4avnuuza3">{m === 'thuis' ? 'Bij mij thuis' : 'Online'}</span>
              <span className="block text-[13px] text-ink-muted" data-id="element-561" data-mp="592y6wt94sl7">{m === 'thuis' ? '45 minuten, incl. inspectie' : '30 minuten via video'}</span>
            </label>)}
        </div>
      </fieldset>

      <fieldset className="mt-8" data-id="element-562" data-mp="2pv4koe08xy0r">
        <legend className="text-[15px] font-medium text-ink" data-id="element-563" data-mp="25vmfooshreud">Kies een dag</legend>
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6" data-id="element-564" data-mp="21u12f2c1oyjn">
          {days.map((d, i) => <label key={d.toISOString()} className={`flex cursor-pointer flex-col items-center rounded-2xl border py-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${i === dayIndex ? 'border-ink bg-ink text-canvas' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-565" data-mp="1x2j30ipo760s" data-mpi={i}>
              <input type="radio" name="day" className="sr-only" checked={i === dayIndex} onChange={() => setDayIndex(i)} data-id="element-566" data-mp="1i7whnk7m09p6" />
              <span className={`text-[12px] capitalize ${i === dayIndex ? 'text-canvas/70' : 'text-ink-muted'}`} data-id="element-567" data-mp="1953qri2c9zzv">
                {format(d, 'EEE', {
              locale: nl
            })}
              </span>
              <span className="text-xl font-semibold tabular-nums" data-id="element-568" data-mp="38fvbj6wdkcta">{format(d, 'd')}</span>
              <span className={`text-[12px] ${i === dayIndex ? 'text-canvas/70' : 'text-ink-muted'}`} data-id="element-569" data-mp="33h1vb6ppe7pf">{format(d, 'MMM', {
              locale: nl
            })}</span>
            </label>)}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-570" data-mp="3fzw8bnobr2xj">
          {moments.map(m => <label key={m.value} className={`cursor-pointer whitespace-nowrap rounded-xl border py-3 text-center text-[14px] font-medium has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${moment === m.value ? 'border-ink text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/40'}`} data-id="element-571" data-mp="3ksz1hup6wad7">
              <input type="radio" name="moment" className="sr-only" checked={moment === m.value} onChange={() => setMoment(m.value)} data-id="element-572" data-mp="320whc9dcaby7" />
              {m.label}
            </label>)}
        </div>
      </fieldset>

      <div className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-573" data-mp="hb72hwcmyq5i">
        <div className="sm:col-span-2" data-id="element-574" data-mp="lcsoc775eeru">
          <label htmlFor="c-name" className="mb-1.5 block text-[14px] font-medium text-ink" data-id="element-575" data-mp="lbqz8ytarrr4">Naam</label>
          <input id="c-name" autoComplete="name" className={input(!!errors.name)} value={form.name} onChange={e => set('name', e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'c-name-err' : undefined} data-id="element-576" data-mp="17fdumjjmpoe5" />
          {errors.name && <p id="c-name-err" className="mt-1.5 text-[13px] text-danger" data-id="element-577" data-mp="1cdf9stdxp3kc">{errors.name}</p>}
        </div>
        <div data-id="element-578" data-mp="1q4u43wdakr6p">
          <label htmlFor="c-email" className="mb-1.5 block text-[14px] font-medium text-ink" data-id="element-579" data-mp="1q4c0geoavaor">E-mail</label>
          <input id="c-email" type="email" autoComplete="email" className={input(!!errors.email)} value={form.email} onChange={e => set('email', e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'c-email-err' : undefined} data-id="element-580" data-mp="1l58wyynyxciw" />
          {errors.email && <p id="c-email-err" className="mt-1.5 text-[13px] text-danger" data-id="element-581" data-mp="5p09ctqxmrr7">{errors.email}</p>}
        </div>
        <div data-id="element-582" data-mp="qyuym4gbgm2">
          <label htmlFor="c-phone" className="mb-1.5 block text-[14px] font-medium text-ink" data-id="element-583" data-mp="rgvhuy5cgzs">Telefoon</label>
          <input id="c-phone" type="tel" autoComplete="tel" className={input(!!errors.phone)} value={form.phone} onChange={e => set('phone', e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'c-phone-err' : undefined} data-id="element-584" data-mp="2cf3krrat6dqb" />
          {errors.phone && <p id="c-phone-err" className="mt-1.5 text-[13px] text-danger" data-id="element-585" data-mp="27mitoxjaeeqh">{errors.phone}</p>}
        </div>
        <div className="sm:col-span-2" data-id="element-586" data-mp="1tn1dv7nyybt9">
          <label htmlFor="c-note" className="mb-1.5 block text-[14px] font-medium text-ink" data-id="element-587" data-mp="1tlzorza4bosj">
            Opmerking <span className="font-normal text-ink-muted" data-id="element-588" data-mp="1xq7i69ritsx3">(optioneel)</span>
          </label>
          <textarea id="c-note" rows={3} className="w-full rounded-xl border border-line bg-white px-4 py-3 text-[16px] text-ink outline-none transition-colors duration-150 focus:border-ink" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Bijv. de meterkast zit in de garage" data-id="element-589" data-mp="1jqy0t6rkwsrj" />
        </div>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3 text-[14px] text-ink-soft" data-id="element-590" data-mp="a9hhwo2yvmsb">
        <input type="checkbox" checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-0.5 h-5 w-5 accent-[#0E1B16]" data-id="element-591" data-mp="f06902aqihqx" />
        <span data-id="element-592" data-mp="1a2s7qumjg8v">
          Ik ga akkoord dat Stroomkluis contact met me opneemt over dit advies.
          {errors.consent && <span className="mt-1 block text-[13px] text-danger" data-id="element-593" data-mp="68e5yco1iawd">{errors.consent}</span>}
        </span>
      </label>

      <div className="mt-10 flex items-center justify-between" data-id="element-594" data-mp="tvcemo5x4ejq">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 px-2 py-3 text-[15px] font-medium text-ink-soft hover:text-ink" data-id="element-595" data-mp="xxqz1cp8jmd4">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-596" data-mp="oydmuc9q8jop" /> Terug
        </button>
        <button type="submit" disabled={status === 'submitting'} className="inline-flex min-w-[210px] items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-ink px-6 py-4 text-[16px] font-medium text-canvas transition-colors duration-150 hover:bg-forest disabled:opacity-70" data-id="element-597" data-mp="p06y64vabs31">
          {status === 'submitting' ? <>
              <LoaderCircleIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-598" data-mp="2x83d0axy1f7z" /> Bezig met inplannen
            </> : 'Bevestig afspraak'}
        </button>
      </div>
    </form>;
}